/**
 * Draft persistence so an interrupted checkout can be resumed.
 *
 * Stored in `localStorage` (survives a refresh, a closed tab and a session
 * timeout). Card details and passwords are never persisted, and a draft older
 * than `MAX_DRAFT_AGE_MS` is discarded so personal data doesn't linger on a
 * shared browser.
 */

import { CHECKOUT_STEPS, emptyDraft, type CheckoutDraft, type CheckoutStepId } from './types';

const STORAGE_KEY = 'aivia.checkout.draft.v1';

/** Drafts expire after 7 days. */
const MAX_DRAFT_AGE_MS = 7 * 24 * 60 * 60 * 1000;

const isCheckoutStep = (value: unknown): value is CheckoutStepId =>
  typeof value === 'string' && (CHECKOUT_STEPS as readonly string[]).includes(value);

const isExpired = (savedAt: unknown): boolean => {
  if (typeof savedAt !== 'string') return false;
  const saved = Date.parse(savedAt);
  return Number.isFinite(saved) && Date.now() - saved > MAX_DRAFT_AGE_MS;
};

export const loadDraft = (): CheckoutDraft | null => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<CheckoutDraft>;
    const base = emptyDraft();

    if (isExpired(parsed.savedAt)) {
      clearDraft();
      return null;
    }

    return {
      ...base,
      ...parsed,
      // A hand-edited or schema-shifted entry must not restore an unknown step.
      step: isCheckoutStep(parsed.step) ? parsed.step : base.step,
      // Passwords are never persisted, so they always come back empty.
      login: { ...base.login, ...parsed.login, password: '', confirmPassword: '' },
      contact: {
        ...base.contact,
        ...parsed.contact,
        billing: { ...base.contact.billing, ...parsed.contact?.billing },
        mailing: { ...base.contact.mailing, ...parsed.contact?.mailing },
      },
      accelerator: { ...base.accelerator, ...parsed.accelerator },
      agreement: { ...base.agreement, ...parsed.agreement },
    };
  } catch {
    return null;
  }
};

export const saveDraft = (draft: CheckoutDraft): void => {
  try {
    // The plaintext password never reaches storage (card details are only ever in Stripe's element).
    const { login, ...persisted } = draft;
    const { password: _password, confirmPassword: _confirmPassword, ...safeLogin } = login;
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...persisted, login: safeLogin, savedAt: new Date().toISOString() }),
    );
  } catch {
    // Storage unavailable (private mode / quota) — checkout still works in-session.
  }
};

export const clearDraft = (): void => {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};
